import { apiZodios } from '~api/apiZodios'
import { setUserAdHocPermissions, setUserPermissions } from '~redux/slices/authSlice'
import { store } from '~redux/store'
import {
  type AccessAction,
  AccessActionEnum,
  type ExternalRoleAssignment,
  GeneralResourceEnum,
  type GroupId,
  type IdentityId,
  type UserId,
} from '~types'

const PERMISSIONS_CACHE_TTL_MS = 5 * 60 * 1000

let inflight: { userId: UserId; promise: Promise<Record<string, string[]>> } | null = null

export const fetchPermissions = (userId: UserId): Promise<Record<string, string[]>> => {
  if (!inflight || inflight.userId !== userId) {
    const promise: Promise<Record<string, string[]>> = apiZodios
      .permissions_listUserPermittedOrgIdentities({ user_id: userId })
      .then(
        (res: { organizations_for_policies: Record<string, string[]> }) =>
          res.organizations_for_policies,
      )
      .finally(() => {
        if (inflight?.promise === promise) inflight = null
      })
    inflight = { userId, promise }
  }
  return inflight.promise
}

type AdHocPermissions = {
  // Keyed by "{resource_type}:{action}" (e.g. "case:read") -> organization identity ids.
  permittedForOrganization: Record<string, string[]>
  // Keyed by "{resource_type}:{action}" (e.g. "case:read") -> resource ids.
  permittedForResource: Record<string, string[]>
}

let adHocInflight: { userId: UserId; promise: Promise<AdHocPermissions> } | null = null

export const fetchAdHocPermissions = (userId: UserId): Promise<AdHocPermissions> => {
  if (!adHocInflight || adHocInflight.userId !== userId) {
    const promise: Promise<AdHocPermissions> = apiZodios
      .permissions_listUserAdHocPermissions({ user_id: userId })
      .then((res) => ({
        permittedForOrganization: res.permitted_for_organization,
        permittedForResource: res.permitted_for_resource,
      }))
      .finally(() => {
        if (adHocInflight?.promise === promise) adHocInflight = null
      })
    adHocInflight = { userId, promise }
  }
  return adHocInflight.promise
}

async function loadAdHocPermissions(userId: UserId): Promise<AdHocPermissions> {
  const cached = store.getState().auth.userAdHocPermissions
  const isStale =
    cached == null ||
    cached.resourcePermissions == null ||
    cached.timestamp < Date.now() - PERMISSIONS_CACHE_TTL_MS
  const isCurrentUser = store.getState().auth.userId === userId
  if (!isStale && isCurrentUser) {
    return {
      permittedForOrganization: cached.permissions,
      permittedForResource: cached.resourcePermissions,
    }
  }

  const permissions = await fetchAdHocPermissions(userId)
  if (store.getState().auth.userId === userId) {
    store.dispatch(
      setUserAdHocPermissions({
        timestamp: Date.now(),
        permissions: permissions.permittedForOrganization,
        resourcePermissions: permissions.permittedForResource,
      }),
    )
  }
  return permissions
}

export async function loadAdHocPermittedResourceIds(
  userId: UserId,
): Promise<Record<string, string[]>> {
  const { permittedForResource } = await loadAdHocPermissions(userId)
  return permittedForResource
}

export type CaseAdHocTargets = {
  caseId: string
  processId?: string | null
  blockIds?: readonly string[]
  blockInstanceIds?: readonly string[]
}

function hasDirectAccessToCase(
  permittedForResource: Record<string, string[]>,
  action: string,
  targets: CaseAdHocTargets,
): boolean {
  const permits = (targetResource: string, ids: readonly string[]): boolean => {
    if (ids.length === 0) return false
    const permitted = new Set(permittedForResource[`${targetResource}:${action}`] ?? [])
    return ids.some((id) => permitted.has(id))
  }

  return (
    permits('case', [targets.caseId]) ||
    permits('process_case', targets.processId ? [targets.processId] : []) ||
    permits('process_case_block', targets.blockIds ?? []) ||
    permits('case_block_instance', targets.blockInstanceIds ?? [])
  )
}

export async function userHasCaseAccess(
  userId: UserId,
  action: AccessAction,
  ownerIdentityId: IdentityId,
  targets: CaseAdHocTargets,
): Promise<boolean> {
  const [orgWidePermissions, permittedForResource] = await Promise.all([
    loadResourcePermittedOrgIdentities(userId, 'cases'),
    loadAdHocPermittedResourceIds(userId),
  ])
  return (
    (orgWidePermissions[action] ?? []).includes(ownerIdentityId) ||
    hasDirectAccessToCase(permittedForResource, action, targets)
  )
}

const AD_HOC_TARGET_RESOURCES: Record<string, string[]> = {
  cases: ['case', 'process_case', 'case_block_instance', 'process_case_block'],
  processes: ['process_case'],
}

export async function loadResourceAdHocPermittedOrgIdentities(
  userId: UserId,
  resource: string | string[],
): Promise<Record<string, IdentityId[]>> {
  const accessLists: Record<string, IdentityId[]> = { read: [], write: [], export: [], delete: [] }
  const resources = Array.isArray(resource) ? resource : [resource]
  const targetResources = new Set(resources.flatMap((r) => AD_HOC_TARGET_RESOURCES[r] ?? []))
  if (targetResources.size === 0) return accessLists

  const { permittedForOrganization } = await loadAdHocPermissions(userId)

  for (const action of ['read', 'write']) {
    const ids = new Set<IdentityId>()
    for (const targetResource of targetResources) {
      for (const orgId of permittedForOrganization[`${targetResource}:${action}`] ?? []) {
        ids.add(orgId as IdentityId)
      }
    }
    accessLists[action] = [...ids]
  }
  return accessLists
}

// Returns whether the user has general permission for resource type in the organization, or ad
// hoc permission to any resource of that type in the organization.
export async function loadResourcePermittedOrgIdentitiesWithAdHoc(
  userId: UserId,
  resource: string | string[],
): Promise<Record<string, IdentityId[]>> {
  const [orgWide, adHoc] = await Promise.all([
    loadResourcePermittedOrgIdentities(userId, resource),
    loadResourceAdHocPermittedOrgIdentities(userId, resource),
  ])
  const merged: Record<string, IdentityId[]> = { read: [], write: [], export: [], delete: [] }
  for (const action of Object.keys(merged)) {
    merged[action] = [...new Set([...(orgWide[action] ?? []), ...(adHoc[action] ?? [])])]
  }
  return merged
}

export async function loadResourcePermittedOrgIdentities(
  userId: UserId,
  resource: string | string[],
): Promise<Record<string, IdentityId[]>> {
  const userPermissionsObj = store.getState().auth.userPermissions
  const permissionsStale =
    userPermissionsObj == null ||
    userPermissionsObj.timestamp < Date.now() - PERMISSIONS_CACHE_TTL_MS
  const orgIdentityList =
    permissionsStale || store.getState().auth.userId !== userId
      ? await fetchPermissions(userId)
      : userPermissionsObj.permissions

  if (permissionsStale && store.getState().auth.userId === userId) {
    store.dispatch(
      setUserPermissions({
        timestamp: Date.now(),
        permissions: orgIdentityList as Record<string, IdentityId[]>,
      }),
    )
  }

  const accessLists: Record<string, IdentityId[]> = { read: [], write: [], export: [], delete: [] }
  const resources = Array.isArray(resource) ? resource : [resource]
  for (const action of Object.keys(accessLists)) {
    const ids = new Set<IdentityId>()
    for (const r of resources) {
      const actionKey = `${r}:${action}`
      if (actionKey in orgIdentityList) {
        for (const id of orgIdentityList[actionKey]) {
          ids.add(id as IdentityId)
        }
      }
    }
    accessLists[action] = [...ids]
  }
  return accessLists
}

// Policy keys (e.g. "cases:read") for every action on a given resource.
const allActionsFor = (resource: string): string[] =>
  AccessActionEnum.options.map((action) => `${resource}:${action}`)

// A group in another organization that one of this organization's roles is assigned to.
export const groupAdHocPendingChanges = (
  pendingChanges: Map<UserId, Map<AccessAction, 'add' | 'remove'>>,
): {
  adds: { actions: AccessAction[]; targetUserIds: UserId[] }[]
  removes: { action: AccessAction; targetUserId: UserId }[]
} => {
  const addsByActions = new Map<string, { actions: AccessAction[]; targetUserIds: UserId[] }>()
  const removes: { action: AccessAction; targetUserId: UserId }[] = []
  for (const [targetUserId, changes] of pendingChanges) {
    const addActions: AccessAction[] = []
    for (const [action, changeType] of changes) {
      if (changeType === 'add') {
        addActions.push(action)
      } else {
        removes.push({ action, targetUserId })
      }
    }
    if (addActions.length > 0) {
      addActions.sort()
      const actionsKey = addActions.join(',')
      if (!addsByActions.has(actionsKey)) {
        addsByActions.set(actionsKey, { actions: addActions, targetUserIds: [] })
      }
      addsByActions.get(actionsKey)!.targetUserIds.push(targetUserId)
    }
  }
  return { adds: Array.from(addsByActions.values()), removes }
}

export type CrossOrganizationAssignment = {
  groupId: GroupId
  groupName: string
  organizationName: string
}

export const groupCrossOrganizationAssignments = (
  externalAssignments: ExternalRoleAssignment[],
): Record<string, CrossOrganizationAssignment[]> => {
  const byRole: Record<string, CrossOrganizationAssignment[]> = {}
  externalAssignments.forEach((assignment) => {
    byRole[assignment.role_id] = [
      ...(byRole[assignment.role_id] ?? []),
      {
        groupId: assignment.group_id as GroupId,
        groupName: assignment.group_name,
        organizationName: assignment.group_organization_name,
      },
    ]
  })
  return byRole
}

// Resources hidden from role creation/editing for cross-organization roles.
export const CROSS_ORGANIZATION_EXCLUDED_RESOURCES = [
  'user_management_settings',
  'organization_settings',
  'assistant_settings',
]

// Predefined policy sets ("resource:action" keys) offered as starting points in role creation.
export const TEMPLATE_ROLES: Record<string, string[]> = {
  Staff: [
    ...allActionsFor('cases'),
    ...allActionsFor('processes'),
    ...allActionsFor('licenses'),
    ...allActionsFor('contacts'),
    ...allActionsFor('payments'),
    ...allActionsFor('assets'),
  ],
  'Finance Admin': [
    ...allActionsFor('payments'),
    ...allActionsFor('lookup_payments'),
    ...allActionsFor('payouts'),
    'cases:read',
    'processes:read',
    'contacts:read',
    'assets:read',
  ],
  'Super Admin': GeneralResourceEnum.options.flatMap((resource) => allActionsFor(resource)),
  'Knowledge Base Admin (PII)': [
    ...allActionsFor('interaction_logs'),
    ...allActionsFor('interaction_logs_pii'),
    ...allActionsFor('interaction_analytics'),
    ...allActionsFor('interaction_customization'),
    ...allActionsFor('knowledge_base'),
    ...allActionsFor('user_management_settings'),
    'cases:read',
    'processes:read',
    'contacts:read',
    'assets:read',
  ],
  'Knowledge Base Manager': [
    ...allActionsFor('interaction_logs'),
    ...allActionsFor('interaction_analytics'),
    ...allActionsFor('interaction_customization'),
    ...allActionsFor('knowledge_base'),
    'cases:read',
    'processes:read',
    'contacts:read',
    'assets:read',
  ],
  'Account Provision': allActionsFor('user_management_settings'),
  'Org Management': [
    ...allActionsFor('organization_settings'),
    ...allActionsFor('user_management_settings'),
    ...allActionsFor('assistant_settings'),
  ],
}
